import {
  createApi,
  fetchBaseQuery,
  type BaseQueryFn,
  type FetchArgs,
  type FetchBaseQueryError,
} from "@reduxjs/toolkit/query/react";

import { toLoginRedirect } from "@/feature/auth/lib/auth-redirect";
import { clearPersistedAuthSession } from "@/feature/auth/lib/auth-session";
import { logout } from "@/feature/auth/state/auth-slice";

let isUnauthorizedLogoutInProgress = false;

const rawBaseQuery = fetchBaseQuery({
  baseUrl: "/api/bff",
  credentials: "include",
  prepareHeaders: (headers, { getState }) => {
    headers.set("Accept", "application/json");

    const state = getState() as {
      auth?: {
        accessToken?: string | null;
      };
    };
    const accessToken = state.auth?.accessToken;

    if (accessToken) {
      headers.set("Authorization", `Bearer ${accessToken}`);
    }

    return headers;
  },
});

const baseQueryWithAuth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const result = await rawBaseQuery(args, api, extraOptions);

  const state = api.getState() as {
    auth?: {
      accessToken?: string | null;
    };
  };
  const wasAuthenticated = Boolean(state.auth?.accessToken);

  if (result.error?.status === 401 && wasAuthenticated) {
    api.dispatch(logout());
    api.dispatch({ type: "baseApi/resetApiState" });
    void clearPersistedAuthSession();

    if (!isUnauthorizedLogoutInProgress && typeof window !== "undefined") {
      isUnauthorizedLogoutInProgress = true;

      const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      window.location.replace(toLoginRedirect(currentPath));
    }
  }

  return result;
};

export const baseApi = createApi({
  reducerPath: "baseApi",
  baseQuery: baseQueryWithAuth,
  tagTypes: [
    "AuthSession",
    "AuthProfile",
    "SupportTicket",
    "SupportTicketList",
    "Traveler",
    "TravelerList",
    "FlightBooking",
    "FlightBookingList",
    "HotelBooking",
    "HotelBookingList",
    "VisaBooking",
    "VisaBookingList",
    "UmrahBooking",
    "UmrahBookingList",
    "CustomUmrahRequest",
    "CustomUmrahRequestList",
    "HolidayBooking",
    "HolidayBookingList",
    "Invoice",
    "InvoiceList",
    "Notification",
    "NotificationList",
    "NotificationUnreadCount",
    "HOTEL_ROOM",
    "HOTEL_SEARCH",
    "HOLIDAY_SEARCH",
    "VISA_SEARCH",
    "UMRAH_SEARCH",
  ],
  endpoints: () => ({}),
});
